import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { usersApi, User } from '../services/usersApi';

interface MyContextData {
  screen: string;
  drawerWidth: number;
  user: User | null;
  loading: boolean;
  fetchUser: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const MyContext = createContext<MyContextData | undefined>(undefined);

interface MyContextProviderProps {
  children: ReactNode;
  initialScreen?: string;
  initialDrawerWidth?: number;
}

export const MyContextProvider: React.FC<MyContextProviderProps> = ({ 
  children, 
  initialScreen = "leads", 
  initialDrawerWidth = 80 
}) => {
  const [screen] = useState(initialScreen);
  const [drawerWidth] = useState(initialDrawerWidth);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Start as true since we'll fetch user data

  const fetchUser = async () => {
    try {
      setLoading(true);
      
      const userId = localStorage.getItem('UserId');
      const token = localStorage.getItem('AccessToken');
      
      if (!userId || !token) {
        setUser(null);
        setLoading(false);
        return;
      }

      const userData = await usersApi.getUserById(userId);
      setUser(userData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    
    const initUser = async () => {
      try {
        setLoading(true);
        
        const userId = localStorage.getItem('UserId');
        const token = localStorage.getItem('AccessToken');
        
        if (!userId || !token) {
          if (!abortController.signal.aborted) {
            setUser(null);
            setLoading(false);
          }
          return;
        }

        const userData = await usersApi.getUserById(userId);
        
        // Only update state if component is still mounted
        if (!abortController.signal.aborted) {
          setUser(userData);
          setLoading(false);
        }
      } catch (error) {
        console.error('Context: Error fetching user:', error);
        if (!abortController.signal.aborted) {
          setUser(null);
          setLoading(false);
        }
      }
    };

    initUser();

    // Cleanup function to abort request if component unmounts
    return () => {
      abortController.abort();
    };
  }, []); // Remove hasFetchedUser dependency to ensure it runs

  const contextValue: MyContextData = {
    screen,
    drawerWidth,
    user,
    loading,
    fetchUser,
    setUser
  };

  return (
    <MyContext.Provider value={contextValue}>
      {children}
    </MyContext.Provider>
  );
};

export const useMyContext = () => {
  const context = useContext(MyContext);
  if (!context) {
    throw new Error('useMyContext must be used within a MyContextProvider');
  }
  return context;
};

export default MyContext;
